import React from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'

import { CuppingFormConfig, CuppingDescriptor, CuppingFormField } from '../../types/cuppingFormConfig'
import RadioButton from './radioButton'
import { CuppingSessionResultSample } from '../../types/cuppingResult'
import Descriptors from './descriptors'
import { CuppingFieldType } from '../../types/enums/cuppingFieldType'
import Rating from './rating'
import Slider from './slider'
import RoastLevel from './roastLevel'
import Checkbox from './checkbox'

interface CuppingScoreFormProps {
  sampleLabel: JSX.Element
  resultSample: CuppingSessionResultSample
  cuppingFormConfig: CuppingFormConfig
  availableDescriptors: Array<CuppingDescriptor>
  totalScore: number
  onUpdateScore: (sampleId: number, fieldId: number, newScore: number) => void
  onUpdateDescriptors: (sampleId: number, newDescriptors: Array<CuppingDescriptor>) => void
  onUpdateNotes: (sampleId: number, newNotes: string) => void
}

const CuppingScoreForm = (props: CuppingScoreFormProps): JSX.Element => {
  const { t } = useTranslation()

  const renderField = (field: CuppingFormField): JSX.Element | null => {
    const fieldProps = {
      key: field.id,
      value: props.resultSample.scores.find((score) => score.cupping_form_field === field.id)?.score || 0,
      label: field.field_label,
      options: field.field_values,
      onChange: (newValue: number) => {
        props.onUpdateScore(props.resultSample.cupping_session_sample || 0, field.id, newValue)
      },
    }
    if (field.field_type === CuppingFieldType.RadioButton) {
      return <RadioButton {...fieldProps} groupName={`${field.id}`} />
    } else if (field.field_type === CuppingFieldType.Stars) {
      return <Rating {...fieldProps} />
    } else if (field.field_type === CuppingFieldType.Slider) {
      return <Slider {...fieldProps} />
    } else if (field.field_type === CuppingFieldType.RoastLevel) {
      return <RoastLevel {...fieldProps} />
    } else if (field.field_type === CuppingFieldType.Checkbox) {
      return <Checkbox {...fieldProps} />
    }
    return null
  }

  return (
    <Stack sx={{ mb: 15 }}>
      <Typography
        variant="h5"
        sx={{
          position: 'sticky',
          top: -1,
          zIndex: 7,
          backgroundColor: 'background.default',
        }}
      >
        {props.sampleLabel}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" flexWrap="wrap" mt={2}>
        {props.cuppingFormConfig.form_fields.map((field) => renderField(field))}
      </Stack>
      {props.cuppingFormConfig.enable_descriptors && (
        <Descriptors
          selectedDescriptors={props.resultSample.descriptors || []}
          suggestedDescriptors={props.availableDescriptors}
          onChange={(newDescriptors: Array<CuppingDescriptor>) => {
            props.onUpdateDescriptors(props.resultSample.cupping_session_sample || 0, newDescriptors)
          }}
        />
      )}
      {props.cuppingFormConfig.enable_notes && (
        <TextField
          sx={{ mt: 2 }}
          label={t('cupping.notes')}
          multiline={true}
          minRows={2}
          value={props.resultSample.notes}
          inputProps={{ maxLength: 2047 }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            props.onUpdateNotes(props.resultSample.cupping_session_sample || 0, event.target.value)
          }}
        />
      )}
      {!!props.cuppingFormConfig.score_calculation && (
        <Typography sx={{ mt: 1 }} variant="h5">
          {t('cupping.totalscorenumber').replace('{0}', `${props.totalScore}`)}
        </Typography>
      )}
    </Stack>
  )
}

export default CuppingScoreForm
